import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useAuth } from "../hooks/useAuth";
import { supabase } from "../lib/supabase";
import { getDefaultAccreditationType } from "../utils/courseAccreditation";
import {
  getCartStorageKey,
  loadCartState,
  needsRefresherParent,
} from "../components/cart/cartState";

const CartContext = createContext();

const CART_DEFAULTS = {
  cartItems: [],
  addToCart: () => ({ success: false, message: "Cart not available" }),
  removeFromCart: () => {},
  clearCart: () => {},
  isInCart: () => false,
  getCartCount: () => 0,
  getCartTotal: () => 0,
  isCartOpen: false,
  setCartOpen: () => {},
  discountCode: "",
  discountInfo: null,
  applyDiscount: () => {},
  removeDiscount: () => {},
  updateAccreditationType: () => {},
  refresherPricingPending: false,
  refresherPricingError: false,
};

export const useCart = () => {
  const context = useContext(CartContext);
  return context || CART_DEFAULTS;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setCartOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);

  // The storage key the in-memory cart belongs to. Saves are skipped until
  // the cart for the current user has been loaded, so one account's cart or
  // discount is never written under another account's (or the guest) key.
  const [cartOwnerKey, setCartOwnerKey] = useState(null);
  const currentKey = getCartStorageKey(user?.id);

  // Load the cart whenever the signed-in user changes (login, logout, switch).
  useEffect(() => {
    const state = loadCartState(localStorage, user?.id || null, (item) => ({
      ...item,
      accreditationType: getDefaultAccreditationType(
        item,
        item.accreditationType,
      ),
    }));
    setCartItems(state.items);
    setDiscountCode(state.discountCode);
    setDiscountInfo(state.discountInfo);
    setCartOwnerKey(state.key);
  }, [user?.id]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cartOwnerKey !== currentKey) return;
    localStorage.setItem(cartOwnerKey, JSON.stringify(cartItems));
  }, [cartItems, cartOwnerKey, currentKey]);

  // Save discount to localStorage whenever it changes
  useEffect(() => {
    if (cartOwnerKey !== currentKey) return;
    if (discountCode && discountInfo) {
      localStorage.setItem(
        `${cartOwnerKey}_discount`,
        JSON.stringify({ code: discountCode, info: discountInfo }),
      );
    } else {
      localStorage.removeItem(`${cartOwnerKey}_discount`);
    }
  }, [discountCode, discountInfo, cartOwnerKey, currentKey]);

  // Refreshers are charged round(parent price x 0.7) by the servers. Items
  // added without their parent's prices (catalog, frameworks, suggestions,
  // older saved carts) get them here so the cart total matches checkout.
  const missingParentIds = useMemo(
    () => [
      ...new Set(
        cartItems.filter(needsRefresherParent).map((i) => i.parent_course_id),
      ),
    ],
    [cartItems],
  );
  const [refresherPricingError, setRefresherPricingError] = useState(false);
  useEffect(() => {
    if (missingParentIds.length === 0) {
      setRefresherPricingError(false);
      return undefined;
    }
    let cancelled = false;
    supabase
      .from("courses")
      .select("id, title, price_inr, price_usd, price_cad")
      .in("id", missingParentIds)
      .then(({ data, error }) => {
        if (cancelled) return;
        const parentsById = new Map((data || []).map((p) => [p.id, p]));
        if (error || parentsById.size < missingParentIds.length) {
          setRefresherPricingError(true);
        }
        if (parentsById.size === 0) return;
        setCartItems((prev) =>
          prev.map((item) =>
            needsRefresherParent(item) && parentsById.has(item.parent_course_id)
              ? {
                  ...item,
                  parentCourseData: parentsById.get(item.parent_course_id),
                }
              : item,
          ),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [missingParentIds.join(",")]);
  const refresherPricingPending = missingParentIds.length > 0;

  const addToCart = useCallback((course, accreditationType) => {
    const effectiveType = getDefaultAccreditationType(
      course,
      accreditationType,
    );
    const sourceFields = [
      "partner_id",
      "partner_slug",
      "partner_name",
      "checkout_source_hostname",
      "checkout_source_url",
    ].reduce((fields, key) => {
      if (course?.[key]) fields[key] = course[key];
      return fields;
    }, {});

    setCartItems((prev) => {
      const exists = prev.find((item) => item.id === course.id);
      if (exists) {
        return prev.map((item) =>
          item.id === course.id
            ? {
                ...item,
                ...sourceFields,
                ...(course.parentCourseData && !item.parentCourseData
                  ? { parentCourseData: course.parentCourseData }
                  : {}),
                accreditationType: effectiveType,
              }
            : item,
        );
      }
      return [...prev, { ...course, accreditationType: effectiveType }];
    });
    return { success: true, message: "Course added to cart" };
  }, []);

  const updateAccreditationType = useCallback((courseId, accreditationType) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === courseId ? { ...item, accreditationType } : item,
      ),
    );
  }, []);

  const removeFromCart = useCallback((courseId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== courseId));
  }, []);

  // Used after a completed checkout: empties the cart and its discount code,
  // including the stored copy, so a cart loaded later in the same render
  // (auth resolving on the payment success page) cannot restore paid items.
  const clearCart = useCallback(() => {
    setCartItems([]);
    setDiscountCode("");
    setDiscountInfo(null);
    try {
      localStorage.removeItem(currentKey);
      localStorage.removeItem(`${currentKey}_discount`);
    } catch {
      /* storage unavailable */
    }
  }, [currentKey]);

  const isInCart = useCallback(
    (courseId) => {
      return cartItems.some((item) => item.id === courseId);
    },
    [cartItems],
  );

  const getCartCount = useCallback(() => {
    return cartItems.length;
  }, [cartItems]);

  const getCartTotal = useCallback(
    (priceGetter) => {
      return cartItems.reduce((total, item) => {
        const price = priceGetter ? priceGetter(item) : item.price || 0;
        return total + price;
      }, 0);
    },
    [cartItems],
  );

  const applyDiscount = useCallback((code, info) => {
    setDiscountCode(code);
    setDiscountInfo(info);
  }, []);

  const removeDiscount = useCallback(() => {
    setDiscountCode("");
    setDiscountInfo(null);
  }, []);

  const value = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      isInCart,
      getCartCount,
      getCartTotal,
      isCartOpen,
      setCartOpen,
      discountCode,
      discountInfo,
      applyDiscount,
      removeDiscount,
      updateAccreditationType,
      refresherPricingPending,
      refresherPricingError,
    }),
    [
      refresherPricingPending,
      refresherPricingError,
      cartItems,
      isCartOpen,
      discountCode,
      discountInfo,
      addToCart,
      removeFromCart,
      clearCart,
      isInCart,
      getCartCount,
      getCartTotal,
      applyDiscount,
      removeDiscount,
      updateAccreditationType,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
